import { BadRequestException, ConflictException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { createHash } from 'node:crypto';
import { PrismaService } from '@cart/api-data-access';
import { assertKeyBelongsTo } from './media-key.util';
import { countsAgainstQuota } from './platform-storage.constants';
import { MediaAssetService } from './media-asset.service';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';
import { StorageUrlSweepService } from './storage-url-sweep.service';

const BATCH_SIZE = 25;

export type MigrationKind = 'local_to_r2' | 'consolidation' | 'restore';

/**
 * Copies assets from one backend to another.
 *
 * One engine for two jobs that are the same job: retiring local disk (§13,
 * mandatory, runs once per org) and consolidating backends after a merchant
 * switches (§14, optional, merchant-initiated). Both copy, verify by checksum,
 * repoint the registry and leave the source objects in place for a retention
 * window so a rollback is a metadata flip rather than a restore.
 *
 * Resumable by construction: progress lives on the job row and each asset is
 * committed independently, so a 10 GB merchant survives a restart, a deploy,
 * or an outage halfway through.
 */
@Injectable()
export class MediaMigrationService {
  private readonly logger = new Logger(MediaMigrationService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver,
    private readonly assets: MediaAssetService,
    private readonly urlSweep: StorageUrlSweepService
  ) {}

  async createJob(args: {
    organizationId: string;
    sourceConfigId: string;
    targetConfigId: string;
    kind: MigrationKind;
    actorId: string;
  }) {
    const [source, target] = await Promise.all([
      this.resolver.configById(args.sourceConfigId),
      this.resolver.configById(args.targetConfigId),
    ]);

    // Both ends must belong to the caller's org. Without this check a job id
    // is enough to copy another tenant's media into a bucket you control.
    if (source.organizationId !== args.organizationId || target.organizationId !== args.organizationId) {
      throw new BadRequestException('Both storage backends must belong to this organization');
    }
    if (source.id === target.id) {
      throw new BadRequestException('Source and target storage are the same');
    }

    const running = await this.prisma.mediaMigrationJob.findFirst({
      where: { organizationId: args.organizationId, status: { in: ['pending', 'running', 'verifying'] } },
    });
    if (running) {
      throw new ConflictException('A storage migration is already running for this account');
    }

    const totalCount = await this.prisma.mediaAsset.count({
      where: { storageConfigId: source.id, status: { in: ['active', 'trashed'] } },
    });

    return this.prisma.mediaMigrationJob.create({
      data: {
        organizationId: args.organizationId,
        sourceConfigId: source.id,
        targetConfigId: target.id,
        kind: args.kind,
        status: 'pending',
        totalCount,
        createdBy: args.actorId,
      },
    });
  }

  /**
   * Runs one batch and returns whether more work remains.
   *
   * Batching rather than one long call: a single request that copies 10 GB
   * dies to any timeout in the stack, and the retry then has no idea what it
   * already did. Callers loop until `done`.
   */
  async runBatch(jobId: string): Promise<{ done: boolean; copied: number; failed: number }> {
    const job = await this.prisma.mediaMigrationJob.findUnique({ where: { id: jobId } });
    if (!job) throw new NotFoundException(`Migration job ${jobId} not found`);
    if (['completed', 'failed', 'rolled_back'].includes(job.status)) {
      return { done: true, copied: job.copiedCount, failed: job.failedCount };
    }

    if (job.status === 'pending') {
      await this.prisma.mediaMigrationJob.update({
        where: { id: jobId },
        data: { status: 'running', startedAt: new Date() },
      });
    }

    const [source, target] = await Promise.all([
      this.resolver.configById(job.sourceConfigId),
      this.resolver.configById(job.targetConfigId),
    ]);
    const [sourceAdapter, targetAdapter] = await Promise.all([
      this.resolver.forConfig(source, 'migration'),
      this.resolver.forConfig(target, 'migration'),
    ]);

    const batch = await this.prisma.mediaAsset.findMany({
      where: {
        storageConfigId: source.id,
        status: { in: ['active', 'trashed'] },
        ...(job.lastAssetId ? { id: { gt: job.lastAssetId } } : {}),
      },
      orderBy: { id: 'asc' },
      take: BATCH_SIZE,
    });

    if (!batch.length) {
      await this.finish(jobId, source.id);
      return { done: true, copied: job.copiedCount, failed: job.failedCount };
    }

    let copied = 0;
    let failed = 0;
    let lastId = job.lastAssetId;

    for (const asset of batch) {
      lastId = asset.id;
      try {
        const bytes = await sourceAdapter.read(asset.storageKey);

        // Verify what we read before writing it. Copying a corrupted or
        // truncated object and then repointing the registry at it would turn a
        // recoverable source-side problem into a permanent loss.
        if (asset.checksum) {
          const actual = createHash('sha256').update(bytes).digest('hex');
          if (actual !== asset.checksum) {
            throw new Error(`Checksum mismatch on read (expected ${asset.checksum}, got ${actual})`);
          }
        }

        // The key is preserved across backends so nothing else has to be
        // rewritten to find an object, and so a rollback is symmetrical.
        assertKeyBelongsTo(asset.organizationId, asset.storageKey);
        const saved = await targetAdapter.save(bytes, asset.storageKey, asset.mimeType);

        const verifyMeta = await targetAdapter.getMetadata(asset.storageKey);
        if (verifyMeta.sizeBytes !== bytes.length) {
          throw new Error(`Size mismatch after copy (${verifyMeta.sizeBytes} vs ${bytes.length})`);
        }

        const oldUrl = asset.publicUrl;
        const newUrl = asset.publicUrl === null ? null : saved.url;

        await this.prisma.$transaction(async tx => {
          await tx.mediaAsset.update({
            where: { id: asset.id },
            data: {
              storageConfigId: target.id,
              publicUrl: newUrl,
              checksum: asset.checksum ?? saved.checksum ?? null,
            },
          });

          // Counters follow the asset: the source loses it, the target gains
          // it, and the org's quota total only moves if exactly one of the two
          // backends is quota-bearing (platform → BYOS frees quota, BYOS →
          // platform consumes it).
          await tx.merchantStorageConfig.update({
            where: { id: source.id },
            data: {
              assetBytes: { decrement: BigInt(asset.sizeBytes) },
              assetCount: { decrement: 1 },
            },
          });
          await tx.merchantStorageConfig.update({
            where: { id: target.id },
            data: {
              assetBytes: { increment: BigInt(asset.sizeBytes) },
              assetCount: { increment: 1 },
            },
          });

          // Org quota moves only when the asset crosses between quota-bearing
          // storage and merchant-billed storage: platform → BYOS frees the
          // merchant's quota, BYOS → platform consumes it, and same-to-same
          // leaves the total alone. The per-config counters above always move.
          const sourceCounts = countsAgainstQuota(source.mode);
          const targetCounts = countsAgainstQuota(target.mode);
          if (sourceCounts !== targetCounts) {
            await this.assets.applyOrgUsageDelta(
              tx,
              asset.organizationId,
              targetCounts ? asset.sizeBytes : -asset.sizeBytes,
              targetCounts ? 1 : -1
            );
          }
        });

        if (oldUrl && newUrl && oldUrl !== newUrl) {
          await this.urlSweep.rewriteUrl(asset.organizationId, oldUrl, newUrl);
        }

        copied += 1;
      } catch (err) {
        failed += 1;
        this.logger.warn(`Migration ${jobId}: asset ${asset.id} failed — ${err}`);
      }
    }

    const updated = await this.prisma.mediaMigrationJob.update({
      where: { id: jobId },
      data: {
        copiedCount: { increment: copied },
        failedCount: { increment: failed },
        lastAssetId: lastId,
      },
    });

    return { done: false, copied: updated.copiedCount, failed: updated.failedCount };
  }

  /** Loops batches to completion. Safe to call again after any interruption. */
  async runToCompletion(jobId: string, maxBatches = 10000): Promise<void> {
    for (let i = 0; i < maxBatches; i++) {
      const { done } = await this.runBatch(jobId);
      if (done) return;
    }
    this.logger.warn(`Migration ${jobId} hit the batch ceiling — re-run to continue`);
  }

  private async finish(jobId: string, sourceId: string): Promise<void> {
    const remaining = await this.prisma.mediaAsset.count({ where: { storageConfigId: sourceId } });
    const job = await this.prisma.mediaMigrationJob.findUniqueOrThrow({ where: { id: jobId } });

    await this.prisma.mediaMigrationJob.update({
      where: { id: jobId },
      data: {
        status: job.failedCount > 0 ? 'failed' : 'completed',
        finishedAt: new Date(),
        error: job.failedCount > 0 ? `${job.failedCount} object(s) could not be copied` : null,
      },
    });

    // Source objects are deliberately NOT deleted here. They stay for the
    // retention window so a rollback is possible; a separate cleanup step
    // removes them once the merchant has lived with the new backend.
    if (remaining === 0 && job.failedCount === 0) {
      await this.prisma.merchantStorageConfig.updateMany({
        where: { id: sourceId, status: 'retained' },
        data: { status: 'archived' },
      });
      this.logger.log(`Migration ${jobId} complete — source config ${sourceId} archived`);
    }
  }

  async getJob(jobId: string, organizationId: string) {
    const job = await this.prisma.mediaMigrationJob.findUnique({ where: { id: jobId } });
    if (!job || job.organizationId !== organizationId) {
      throw new NotFoundException(`Migration job ${jobId} not found`);
    }
    return job;
  }

  async listJobs(organizationId: string) {
    return this.prisma.mediaMigrationJob.findMany({
      where: { organizationId },
      orderBy: { createdAt: 'desc' },
      take: 20,
    });
  }
}
